/**
 * Email Queue Tab
 * Displays and manages queued email items with stats cards and filters
 * Similar design to VendorManagement list page
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconMail,
  IconEye,
  IconEdit,
  IconRefresh,
  IconArrowsSort,
  IconClock,
  IconFilter,
  IconCheck,
  IconX,
  IconCalendar,
  IconUser,
  IconCalendarTime,
  IconMailOpened,
  IconMailFast,
  IconAlertTriangle,
  IconPaperclip,
} from '@tabler/icons-react';
import {
  Stack,
  Group,
  Text,
  Button,
  ActionIcon,
  Badge,
  Card,
  Paper,
  ThemeIcon,
  TextInput,
  Select,
  Drawer,
  Divider,
  Tooltip,
  Alert,
} from '../../../../../components/ui';
import { SimpleGrid } from '../../../../../components/ui/layout';
import { DatePickerInput } from '@mantine/dates';
import { DataTable } from '../../../../../components/advanced/data';
import type { DataTableColumn } from '../../../../../components/advanced/data/DataTable';
import { EmptyState } from '../../../../../components/ui/data-display';
import { alert } from '../../../../../components/ui/providers/AlertProvider';
import type { EmailQueueTabProps, EmailQueueRow } from '../types';
import { mapEmailToRow } from '../types';
import { formatDateTime, PRIORITIES } from '../../types';

// Stats filter type
type StatsFilter = 'all' | 'sent' | 'failed' | 'pending' | 'scheduled' | 'delivered' | 'opened' | 'bounced' | null;

export function EmailQueueTab({
  queueItems,
  loading,
  onRefresh,
  onView,
  onEdit,
  onRetry,
  onUpdatePriority,
}: EmailQueueTabProps) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filtersDrawerOpened, setFiltersDrawerOpened] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [priorityFilter, setPriorityFilter] = useState<string | null>(null);
  const [dateFromFilter, setDateFromFilter] = useState<Date | null>(null);
  const [dateToFilter, setDateToFilter] = useState<Date | null>(null);
  const [statsFilter, setStatsFilter] = useState<StatsFilter>(null);

  // Transform data for DataTable
  const rows: EmailQueueRow[] = useMemo(() => {
    return queueItems.map(mapEmailToRow);
  }, [queueItems]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = queueItems.length;
    const sent = queueItems.filter((q) => q.isSent && !q.hasError).length;
    const failed = queueItems.filter((q) => q.hasError).length;
    const pending = queueItems.filter((q) => q.isPending).length;
    const scheduled = queueItems.filter((q) => q.isScheduled && !q.isSent).length;
    // SES delivery tracking
    const delivered = queueItems.filter((q) => !!q.deliveredDateTime).length;
    const opened = queueItems.filter((q) => !!q.openedDateTime || !!q.readDateTime).length;
    const bounced = queueItems.filter((q) => !!q.bounceType).length;
    return { total, sent, failed, pending, scheduled, delivered, opened, bounced };
  }, [queueItems]);

  // Helper to parse createdDateTime (format: yyyyMMddHHmmss) to Date
  const parseDateTime = (dt: number | undefined): Date | null => {
    if (!dt) return null;
    const str = String(dt);
    if (str.length === 14) {
      const year = parseInt(str.substring(0, 4));
      const month = parseInt(str.substring(4, 6)) - 1;
      const day = parseInt(str.substring(6, 8));
      return new Date(year, month, day);
    }
    return null;
  };

  // Filter data
  const filteredRows = useMemo(() => {
    let filtered = [...rows];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (row) =>
          row.recipient?.toLowerCase().includes(query) ||
          row.subject?.toLowerCase().includes(query) ||
          row.deliveryStatus?.toLowerCase().includes(query) ||
          row.cc?.toLowerCase().includes(query) ||
          row.bcc?.toLowerCase().includes(query) ||
          row.from?.toLowerCase().includes(query)
      );
    }

    // Stats card filter (takes precedence over status dropdown)
    if (statsFilter) {
      switch (statsFilter) {
        case 'sent':
          filtered = filtered.filter((row) => row.isSent && !row.hasError);
          break;
        case 'failed':
          filtered = filtered.filter((row) => row.hasError);
          break;
        case 'pending':
          filtered = filtered.filter((row) => row.isPending);
          break;
        case 'scheduled':
          filtered = filtered.filter((row) => row.isScheduled && !row.isSent);
          break;
        case 'delivered':
          filtered = filtered.filter((row) => !!row.deliveredDateTime);
          break;
        case 'opened':
          filtered = filtered.filter((row) => !!row.openedDateTime || !!row.readDateTime);
          break;
        case 'bounced':
          filtered = filtered.filter((row) => !!row.bounceType);
          break;
        case 'all':
        default:
          // No filter
          break;
      }
    } else {
      // Regular status filter
      if (statusFilter) {
        filtered = filtered.filter((row) => row.deliveryStatus === statusFilter);
      }
    }

    // Priority filter
    if (priorityFilter) {
      filtered = filtered.filter(
        (row) => row.priority === Number(priorityFilter)
      );
    }

    // Date from filter
    if (dateFromFilter) {
      filtered = filtered.filter((row) => {
        const createdDate = parseDateTime(row.createdDateTime);
        return createdDate && createdDate >= dateFromFilter;
      });
    }

    // Date to filter
    if (dateToFilter) {
      const endOfDay = new Date(dateToFilter);
      endOfDay.setHours(23, 59, 59, 999);
      filtered = filtered.filter((row) => {
        const createdDate = parseDateTime(row.createdDateTime);
        return createdDate && createdDate <= endOfDay;
      });
    }

    // Default sort by ID descending (newest first)
    filtered.sort((a, b) => b.emailID - a.emailID);

    return filtered;
  }, [
    rows,
    searchQuery,
    statusFilter,
    priorityFilter,
    dateFromFilter,
    dateToFilter,
    statsFilter,
  ]);

  // Reset to first page whenever a filter/search changes so we never land on an empty page.
  useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilter, priorityFilter, dateFromFilter, dateToFilter, statsFilter]);

  // Count active filters (excluding search and stats filter)
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (statusFilter) count++;
    if (priorityFilter) count++;
    if (dateFromFilter) count++;
    if (dateToFilter) count++;
    return count;
  }, [statusFilter, priorityFilter, dateFromFilter, dateToFilter]);

  // Handle stats card click
  const handleStatsClick = useCallback((filter: StatsFilter) => {
    setStatsFilter((prev) => (prev === filter ? null : filter));
    setStatusFilter(null); // Clear regular status filter when using stats filter
  }, []);

  // Clear all filters
  const clearAllFilters = () => {
    setStatusFilter(null);
    setPriorityFilter(null);
    setDateFromFilter(null);
    setDateToFilter(null);
    setStatsFilter(null);
  };

  const hasActiveFilters =
    statusFilter ||
    priorityFilter ||
    dateFromFilter ||
    dateToFilter ||
    statsFilter;

  // Handle view email - navigate to view page
  const handleViewEmail = useCallback(
    (row: EmailQueueRow) => {
      navigate(`/admin/communications/email/view/${row.emailID}`);
    },
    [navigate]
  );

  // Define columns
  const columns: DataTableColumn<EmailQueueRow>[] = useMemo(
    () => [
      {
        key: 'emailID',
        label: 'ID',
        width: 80,
        sortable: true,
        render: (value) => (
          <Text size="sm" fw={500}>
            #{value}
          </Text>
        ),
      },
      {
        key: 'recipient',
        label: 'Recipient',
        sortable: true,
        render: (value, row) => (
          <Stack gap={0}>
            <Group gap="xs">
              <ThemeIcon size="sm" variant="light" color="blue">
                <IconUser size={12} />
              </ThemeIcon>
              <Text size="sm" fw={500}>
                {value || 'N/A'}
              </Text>
            </Group>
            {row.cc && (
              <Text size="xs" c="dimmed">
                CC: {row.cc}
              </Text>
            )}
          </Stack>
        ),
      },
      {
        key: 'subject',
        label: 'Subject',
        sortable: true,
        render: (value) => (
          <Text size="sm" lineClamp={1} style={{ maxWidth: 300 }}>
            {value || '(No Subject)'}
          </Text>
        ),
      },
      {
        key: 'deliveryStatus',
        label: 'Status',
        width: 130,
        sortable: true,
        filterable: true,
        render: (value, row) => (
          <Badge
            variant="light"
            color={
              row.hasError
                ? 'red'
                : row.isPending
                  ? 'yellow'
                  : row.isSent
                    ? 'green'
                    : row.isScheduled
                      ? 'blue'
                      : 'gray'
            }
            leftSection={
              row.hasError ? (
                <IconX size={12} />
              ) : row.isSent ? (
                <IconCheck size={12} />
              ) : row.isPending ? (
                <IconClock size={12} />
              ) : row.isScheduled ? (
                <IconCalendarTime size={12} />
              ) : null
            }
          >
            {value}
          </Badge>
        ),
      },
      {
        key: 'priority',
        label: 'Priority',
        width: 100,
        sortable: true,
        render: (value, row) => {
          const priorityValue = value ?? 5;
          const color =
            priorityValue >= 8
              ? 'red'
              : priorityValue >= 6
                ? 'orange'
                : priorityValue >= 4
                  ? 'blue'
                  : 'gray';
          return (
            <Tooltip label={row.priorityName}>
              <Badge variant="outline" color={color} size="sm">
                {priorityValue}
              </Badge>
            </Tooltip>
          );
        },
      },
      {
        key: 'trackingStatus',
        label: 'Tracking',
        width: 130,
        render: (_, row) => {
          if (row.bounceType) return <Badge size="sm" variant="light" color="red" leftSection={<IconAlertTriangle size={12} />}>Bounced</Badge>;
          if (row.unsubscribeDateTime) return <Badge size="sm" variant="light" color="grape">Complained</Badge>;
          if (row.clickedDateTime) return <Badge size="sm" variant="light" color="teal">Clicked</Badge>;
          if (row.openedDateTime || row.readDateTime) return <Badge size="sm" variant="light" color="green" leftSection={<IconMailOpened size={12} />}>Opened</Badge>;
          if (row.deliveredDateTime) return <Badge size="sm" variant="light" color="green" leftSection={<IconMailFast size={12} />}>Delivered</Badge>;
          return <Text size="xs" c="dimmed">—</Text>;
        },
      },
      {
        key: 'attachments',
        label: '📎',
        width: 50,
        render: (_, row) => (row.attachments && row.attachments.length > 0
          ? <Tooltip label={`${row.attachments.length} attachment(s)`}><Group gap={2}><IconPaperclip size={14} /><Text size="xs">{row.attachments.length}</Text></Group></Tooltip>
          : null),
      },
      {
        key: 'timeDisplay',
        label: 'Time',
        width: 160,
        sortable: true,
        render: (value, row) => (
          <Stack gap={0}>
            <Text size="sm">{value || formatDateTime(row.createdDateTime)}</Text>
            {row.isScheduled && row.scheduledFor && (
              <Text size="xs" c="blue">
                Scheduled: {row.scheduledFor}
              </Text>
            )}
          </Stack>
        ),
      },
      {
        key: 'actions',
        label: 'Actions',
        width: 100,
        render: (_, row) => (
          <Group gap="xs">
            <Tooltip label="View Details">
              <ActionIcon
                variant="subtle"
                color="blue"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewEmail(row);
                }}
              >
                <IconEye size={16} />
              </ActionIcon>
            </Tooltip>

            {row.isPending && (
              <Tooltip label="Edit">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Find the original item and pass it to onEdit
                    const originalItem = queueItems.find(
                      (item) => item.emailID === row.emailID
                    );
                    if (originalItem) onEdit(originalItem);
                  }}
                >
                  <IconEdit size={16} />
                </ActionIcon>
              </Tooltip>
            )}

            {row.hasError && (
              <Tooltip label="Retry">
                <ActionIcon
                  variant="subtle"
                  color="green"
                  onClick={async (e) => {
                    e.stopPropagation();
                    const confirmed = await alert.confirm(
                      'Retry Failed Email',
                      `Are you sure you want to retry sending this email to ${row.recipient}?`
                    );
                    if (confirmed) {
                      await onRetry(row.emailID);
                    }
                  }}
                >
                  <IconRefresh size={16} />
                </ActionIcon>
              </Tooltip>
            )}

            {row.isPending && (
              <Tooltip label="Change Priority">
                <ActionIcon
                  variant="subtle"
                  color="orange"
                  onClick={async (e) => {
                    e.stopPropagation();
                    handleChangePriority(row);
                  }}
                >
                  <IconArrowsSort size={16} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        ),
      },
    ],
    [handleViewEmail, onEdit, onRetry, queueItems]
  );

  const handleChangePriority = async (row: EmailQueueRow) => {
    const currentPriority = String(row.priority ?? 5);

    // Simple priority change dialog
    const newPriorityStr = prompt(
      `Current priority: ${currentPriority}\nEnter new priority (1-10):`,
      currentPriority
    );

    if (newPriorityStr) {
      const newPriority = parseInt(newPriorityStr, 10);
      if (newPriority >= 1 && newPriority <= 10) {
        await onUpdatePriority(row.emailID, newPriority);
      } else {
        alert.error('Invalid priority', 'Priority must be between 1 and 10');
      }
    }
  };

  // Stats card component
  const StatsCard = ({
    icon,
    color,
    value,
    label,
    filterValue,
  }: {
    icon: React.ReactNode;
    color: string;
    value: number;
    label: string;
    filterValue: StatsFilter;
  }) => (
    <Card
      withBorder
      padding="md"
      radius="md"
      style={{
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        borderColor:
          statsFilter === filterValue
            ? `var(--mantine-color-${color}-5)`
            : undefined,
        backgroundColor:
          statsFilter === filterValue
            ? `var(--mantine-color-${color}-light)`
            : undefined,
      }}
      onClick={() => handleStatsClick(filterValue)}
    >
      <Group>
        <ThemeIcon
          size="lg"
          radius="md"
          variant={statsFilter === filterValue ? 'filled' : 'light'}
          color={color}
        >
          {icon}
        </ThemeIcon>
        <Stack gap={0}>
          <Text size="xl" fw={700}>
            {value}
          </Text>
          <Text size="xs" c="dimmed">
            {label}
          </Text>
        </Stack>
      </Group>
    </Card>
  );

  if (!loading && queueItems.length === 0) {
    return (
      <EmptyState
        title="No Queued Emails"
        description="Your email queue is empty. Start by creating a test email in the Test tab."
        icon={<IconMail size={48} />}
      />
    );
  }

  return (
    <Stack gap="lg">
      {/* Stats Cards (click to filter) */}
      <SimpleGrid cols={{ base: 2, sm: 4, md: 4, lg: 8 }}>
        <StatsCard icon={<IconMail size={20} />} color="blue" value={stats.total} label="Total" filterValue="all" />
        <StatsCard icon={<IconCheck size={20} />} color="green" value={stats.sent} label="Sent" filterValue="sent" />
        <StatsCard icon={<IconClock size={20} />} color="yellow" value={stats.pending} label="Pending" filterValue="pending" />
        <StatsCard icon={<IconCalendarTime size={20} />} color="cyan" value={stats.scheduled} label="Scheduled" filterValue="scheduled" />
        <StatsCard icon={<IconMailFast size={20} />} color="teal" value={stats.delivered} label="Delivered" filterValue="delivered" />
        <StatsCard icon={<IconMailOpened size={20} />} color="green" value={stats.opened} label="Opened" filterValue="opened" />
        <StatsCard icon={<IconAlertTriangle size={20} />} color="orange" value={stats.bounced} label="Bounced" filterValue="bounced" />
        <StatsCard icon={<IconX size={20} />} color="red" value={stats.failed} label="Failed" filterValue="failed" />
      </SimpleGrid>

      {/* Active Filter Indicator */}
      {statsFilter && (
        <Alert color="blue">
          Showing{' '}
          <Text span fw={600}>
            {statsFilter === 'all'
              ? 'all emails'
              : statsFilter === 'pending'
                ? 'pending emails'
                : statsFilter === 'scheduled'
                  ? 'scheduled emails'
                  : `${statsFilter} emails`}
          </Text>
          . Click the card again or close to clear filter.
        </Alert>
      )}

      {/* Search and Filters Bar */}
      <Paper withBorder p="md" radius="md">
        <Group justify="space-between" wrap="wrap">
          <Group gap="md" wrap="wrap" style={{ flex: 1 }}>
            <TextInput
              placeholder="Search by recipient, subject..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ minWidth: 250 }}
              leftSection={<IconMail size={16} />}
            />
            <Button
              variant={activeFilterCount > 0 ? 'light' : 'default'}
              leftSection={<IconFilter size={16} />}
              onClick={() => setFiltersDrawerOpened(true)}
              color={activeFilterCount > 0 ? 'blue' : undefined}
            >
              Filters
              {activeFilterCount > 0 && (
                <Badge size="sm" variant="filled" color="blue" ml="xs">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
            {hasActiveFilters && (
              <Button
                variant="subtle"
                color="red"
                size="sm"
                onClick={clearAllFilters}
              >
                Clear All
              </Button>
            )}
          </Group>
          <Button
            variant="light"
            leftSection={<IconRefresh size={16} />}
            onClick={onRefresh}
            loading={loading}
          >
            Refresh
          </Button>
        </Group>
      </Paper>

      {/* Results Count */}
      <Text size="sm" c="dimmed">
        Showing {filteredRows.length} of {queueItems.length} emails
      </Text>

      {/* DataTable */}
      <Card withBorder p={0}>
        <DataTable
          data={filteredRows}
          columns={columns}
          loading={loading}
          onRowClick={(row) => handleViewEmail(row)}
          emptyMessage="No emails found"
          striped
          highlightOnHover
          enableInternalSort
          pagination={{
            page,
            pageSize,
            total: filteredRows.length,
            onPageChange: setPage,
            onPageSizeChange: setPageSize,
          }}
        />
      </Card>

      {/* Filters Drawer */}
      <Drawer
        opened={filtersDrawerOpened}
        onClose={() => setFiltersDrawerOpened(false)}
        title={
          <Group>
            <IconFilter size={20} />
            <Text fw={600}>Filters</Text>
          </Group>
        }
        position="right"
        size="sm"
        padding="lg"
      >
        <Stack gap="lg">
          {/* Status Filter */}
          <Select
            label="Status"
            placeholder="All statuses"
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              setStatsFilter(null);
            }}
            clearable
            data={[
              { value: 'Pending', label: 'Pending' },
              { value: 'Sent', label: 'Sent' },
              { value: 'Failed', label: 'Failed' },
              { value: 'Scheduled', label: 'Scheduled' },
            ]}
            disabled={!!statsFilter}
          />

          {/* Priority Filter */}
          <Select
            label="Priority"
            placeholder="All priorities"
            value={priorityFilter}
            onChange={setPriorityFilter}
            clearable
            data={PRIORITIES.map((p) => ({
              value: String(p.value),
              label: p.label,
            }))}
          />

          <Divider label="Date Queued" labelPosition="center" />

          {/* Date From Filter */}
          <DatePickerInput
            label="From Date"
            placeholder="Select start date"
            value={dateFromFilter}
            onChange={setDateFromFilter}
            clearable
            leftSection={<IconCalendar size={14} />}
            maxDate={dateToFilter || undefined}
          />

          {/* Date To Filter */}
          <DatePickerInput
            label="To Date"
            placeholder="Select end date"
            value={dateToFilter}
            onChange={setDateToFilter}
            clearable
            leftSection={<IconCalendar size={14} />}
            minDate={dateFromFilter || undefined}
          />

          <Divider />

          {/* Clear Filters Button */}
          <Group grow>
            <Button
              variant="light"
              color="red"
              onClick={clearAllFilters}
              disabled={!hasActiveFilters}
            >
              Clear All Filters
            </Button>
          </Group>

          {/* Apply Button */}
          <Button fullWidth onClick={() => setFiltersDrawerOpened(false)}>
            Apply Filters
          </Button>
        </Stack>
      </Drawer>
    </Stack>
  );
}
